"use client"
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom"
import { ThemeProvider, createTheme } from "@mui/material"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { motion, AnimatePresence } from "framer-motion"

// Components
import Layout from "./components/Layout"
import Login from "./pages/Login"
import RequestForm from "./pages/RequestForm"
import Profile from "./pages/Profile"
import MyRequestList from "./pages/MyRequestList"
import AllRequestList from "./pages/AllRequestList"
import ManagerRequestList from "./pages/ManagerRequestList"
import AdminOrderCounts from "./pages/AdminOrderCounts"
import UserOrdersPage from "./pages/UserOrdersPage"
import AdminSlotManagement from "./pages/AdminSlotManagement"
import DefaultSlotManagement from "./pages/DefaultSlotManagement"
import CreateOrderPage from "./pages/AdminEmergencyOrders"
import AcknowledgmentPage from "./pages/AcknowledgmentPage" // 🆕 Added acknowledgment page import
import ChatBot from "./components/ChatBot"

// Context
import { AuthProvider } from "./context/AuthContext"
import { SlotsProvider } from "./contexts/SlotsContext"

// Enhanced theme with colors from the provided image
const theme = createTheme({
  palette: {
    primary: {
      main: "#FF8000", // Orange from image
      light: "#FFB366",
      dark: "#CC6600",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#4B2E83", // Purple from image
      light: "#7B5BA6",
      dark: "#2D1B4F",
      contrastText: "#ffffff",
    },
    accent: {
      main: "#16B1C8", // Teal
      light: "#4FC3D7",
      dark: "#0E8A9F",
      contrastText: "#ffffff",
    },
    success: {
      main: "#2E7D32",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#FF8000",
      contrastText: "#000000",
    },
    error: {
      main: "#C62828",
      contrastText: "#ffffff",
    },
    background: {
      default: "#F8FAFC",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1F2937",
      secondary: "#6B7280",
    },
  },
  typography: {
    fontFamily: ["Inter", "Roboto", "Poppins", "sans-serif"].join(","),
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
    },
    h2: {
      fontWeight: 600,
      fontSize: "2rem",
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.75rem",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.125rem",
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 12,
          fontWeight: 600,
          padding: "10px 24px",
          transition: "all 0.2s ease-in-out",
          // Mobile-specific improvements
          '@media (max-width: 768px)': {
            minHeight: '48px',
            padding: '12px 16px',
            fontSize: '0.9rem',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          transition: "box-shadow 0.2s ease-in-out",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            transition: "border-color 0.2s ease-in-out",
            // Mobile-specific improvements
            '@media (max-width: 768px)': {
              '& input, & textarea': {
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 14px',
              },
            },
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#F8FAFC",
          backgroundImage: `
            radial-gradient(circle at 20% 80%, rgba(255, 128, 0, 0.05) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(75, 46, 131, 0.05) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(22, 177, 200, 0.03) 0%, transparent 50%)
          `,
        },
        "*": {
          scrollBehavior: "smooth",
        },
        "::-webkit-scrollbar": {
          width: "8px",
        },
        "::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "10px",
        },
        "::-webkit-scrollbar-thumb": {
          background: "#FF8000",
          borderRadius: "10px",
          "&:hover": {
            background: "#CC6600",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          // Better touch targets on mobile
          '@media (max-width: 768px)': {
            minHeight: '48px',
            minWidth: '48px',
            padding: '12px',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          // Mobile-responsive table cells
          '@media (max-width: 768px)': {
            padding: '8px',
            fontSize: '0.875rem',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          // Mobile-responsive dialogs
          '@media (max-width: 768px)': {
            margin: '16px',
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 32px)',
          },
        },
      },
    },
  },
})

// Role-based default redirect component
const RoleBasedRedirect = () => {
  const getUserRoleFromToken = () => {
    try {
      const token = localStorage.getItem("idToken")
      if (!token) return "employee"

      const payload = token.split(".")[1]
      const decodedPayload = JSON.parse(atob(payload))

      return decodedPayload.user_role || "employee"
    } catch (error) {
      console.error("Error decoding token:", error)
      return "employee"
    }
  }

  const userRole = getUserRoleFromToken()

  // Redirect based on user role
  if (userRole === "admin") {
    return <Navigate to="/admin/order-counts" replace />
  } else {
    return <Navigate to="/my-requests" replace />
  }
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem("idToken")

  if (!isAuthenticated) {
    return <Navigate to="/login" />
  }

  return children
}

// Page transition wrapper
const PageTransition = ({ children }) => children

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <SlotsProvider>
        <Router>
          <ToastContainer
            position="top-right"
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            toastStyle={{
              borderRadius: "12px",
              background: "linear-gradient(135deg, #FF8000 0%, #FFB366 100%)",
              color: "white",
              fontWeight: 600,
            }}
          />
          <AnimatePresence mode="wait">
            <Routes>
              <Route
                path="/login"
                element={
                  <PageTransition>
                    <Login />
                  </PageTransition>
                }
              />

              {/* 🆕 Public acknowledgment route - no authentication required */}
              <Route
                path="/acknowledge"
                element={
                  <PageTransition>
                    <AcknowledgmentPage />
                  </PageTransition>
                }
              />

              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                {/* Updated index route for role-based redirection */}
                <Route index element={<RoleBasedRedirect />} />
                <Route
                  path="requests/new"
                  element={
                    <PageTransition>
                      <RequestForm />
                    </PageTransition>
                  }
                />
                <Route
                  path="profile"
                  element={
                    <PageTransition>
                      <Profile />
                    </PageTransition>
                  }
                />
                <Route
                  path="my-requests"
                  element={
                    <PageTransition>
                      <MyRequestList />
                    </PageTransition>
                  }
                />
                <Route
                  path="all-requests"
                  element={
                    <PageTransition>
                      <AllRequestList />
                    </PageTransition>
                  }
                />
                <Route
                  path="manager-requests"
                  element={
                    <PageTransition>
                      <ManagerRequestList />
                    </PageTransition>
                  }
                />
                <Route
                  path="user-requests"
                  element={
                    <PageTransition>
                      <UserOrdersPage />
                    </PageTransition>
                  }
                />
                <Route
                  path="admin/order-counts"
                  element={
                    <PageTransition>
                      <AdminOrderCounts />
                    </PageTransition>
                  }
                />
                <Route
                  path="admin/slot-management"
                  element={
                    <PageTransition>
                      <AdminSlotManagement />
                    </PageTransition>
                  }
                />
                <Route
                  path="admin/default-slots"
                  element={
                    <PageTransition>
                      <DefaultSlotManagement />
                    </PageTransition>
                  }
                />
                <Route
                  path="admin/emergency-orders"
                  element={
                    <PageTransition>
                      <CreateOrderPage />
                    </PageTransition>
                  }
                />
              </Route>
            </Routes>
          </AnimatePresence>
          
          {/* ChatBot - only show when authenticated */}
          {localStorage.getItem("idToken") && <ChatBot />}
        </Router>
        </SlotsProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App